import FormBlock from '../../components/form/FormBlock.tsx';
import { CreatePromptRequest, LLMPromptConfiguration, Project, Prompt, WorkflowNode } from '../../api/model.ts';
import { getFormSchema, getUiSchema, validator } from '../../api/prompts/formSchema.ts';
import { folderExtraErrors, getFolderPathServerError } from 'utils/folderPath';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import usePrompt from '../../api/prompts/usePrompt.ts';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Loader from '../../components/Loader.tsx';
import { CircularProgress, Typography, MenuItem } from '@mui/material';
import {
  CloseTextButton,
  LimeButton,
  OutlinedActionButton,
  Pane,
  PaneFooter,
  PaneHeader,
  PaneHeaderTitle,
  PaneScroll,
  BlueButton,
  TwoPaneRoot,
} from './PromptEdit.styles';
import { RevisionSelect } from 'components/settingsHub/formHeader.styles';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { toast } from 'react-toastify';
import usePrompts from '../../api/prompts/usePrompts.ts';
import PromptsTestCaseItem from './PromptsTestCaseItem.tsx';
import AddIcon from '@mui/icons-material/Add';
import usePromptTestCases from '../../api/prompts-test-cases/usePromptTestCases.ts';
import useModels from '../../api/prompts-models/useModels.ts';
import Form from '@rjsf/core';
import { useSearchParams } from 'react-router-dom';
import { FaLink, FaListUl } from 'react-icons/fa6';
import { getNodesUsingPrompt } from 'api/prompts/requests.ts';
import { useNavigate } from 'react-router';
import OutdatedRevisionsToast, {
  RevisionField,
  PROMPT_REVISION_FIELD,
  EVALUATION_PROMPT_REVISION_FIELD,
} from 'views/toasts/OutdatedRevisionsToast.tsx';
import useProjects from 'api/projects/useProjects.ts';
import useDialog from 'hooks/useDialog.tsx';
import PromptUsageDialog from 'components/dialog/PromptUsageDialog.tsx';

type Props = {
  promptId: string;
  projectId: string;
  activeWorkflowId?: string;
  requiredInputs?: string[];
  additionalInputsAllowed?: boolean;
  templateHint?: string;
  onCancel: () => void;
  onSubmit: (p: string) => void;
};

const promptRevisionField = (node: WorkflowNode, promptId: string): RevisionField => {
  if (node.type !== 'llm_prompt') {
    return PROMPT_REVISION_FIELD;
  }

  const configuration = node.configuration as LLMPromptConfiguration;
  if (configuration.evaluation_prompt_id === promptId) {
    return EVALUATION_PROMPT_REVISION_FIELD;
  }

  return PROMPT_REVISION_FIELD;
};

const FORM_ID = 'EDIT_PROMPT_FORM';
const PromptEdit = ({
  promptId,
  projectId,
  activeWorkflowId,
  requiredInputs,
  additionalInputsAllowed = true,
  templateHint,
  onCancel,
  onSubmit,
}: Props) => {
  const navigate = useNavigate();
  const [showUsageDialog, UsageDialog] = useDialog(PromptUsageDialog);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRevision = Number(searchParams.get('revision'));
  const {
    prompt: latestPrompt,
    update: { mutateAsync: mutatePrompt, isPending: isUpdatePending },
    promptIsLoading,
    promptIsFetched,
  } = usePrompt({ promptId });
  const { projects, isLoading: areProjectsLoading } = useProjects();

  const latestRevisionSelected = selectedRevision === latestPrompt?.revision;

  const revisionToLoad = selectedRevision || latestPrompt?.revision;

  const {
    prompt: revisedPrompt,
    promptIsLoading: isRevisedPromptLoading,
    promptIsFetched: isRevisedPromptFetched,
  } = usePrompt({
    promptId,
    revision: revisionToLoad,
    prompt: revisionToLoad === latestPrompt?.revision ? latestPrompt : undefined,
  });

  const prompt = latestRevisionSelected ? latestPrompt : revisedPrompt;

  const handleChangeRevision = useCallback(
    (revision: number | string) => {
      setSearchParams((params) => {
        params.set('revision', String(revision));
        return params;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (prompt && !selectedRevision) {
      handleChangeRevision(prompt.revision);
    }
  }, [handleChangeRevision, prompt, selectedRevision]);

  const {
    create: { isPending: isSaveCopyPending, mutateAsync: createPromptCopy },
  } = usePrompts(projectId);

  const { list: testsList, create: createTestCase } = usePromptTestCases(promptId ?? '');
  const { models } = useModels();
  const addNewTest = () => {
    if (models == undefined || models.length == 0) {
      toast.error('Add some prompts model first');
    } else {
      createTestCase.mutate({
        test_case: prompt?.inputs?.reduce((a, v) => ({ ...a, [v]: '' }), {}) ?? {},
        llm_model_id: models.pop()!.id,
      });
    }
  };

  const handleSavePrompt = async (formData: CreatePromptRequest) => {
    setFolderError(null);
    try {
      const newInputs = formData.inputs.filter((input) => !latestPrompt?.inputs.includes(input));
      const removedInputs = latestPrompt?.inputs.filter((input) => !formData.inputs.includes(input)) ?? [];
      const data = await mutatePrompt(formData);
      handleChangeRevision(data.revision);

      if (!latestPrompt?.revision || data.revision <= latestPrompt.revision) {
        return;
      }

      const parentNodes = await getNodesUsingPrompt(promptId);
      if (parentNodes.length > 0) {
        toast.warning(
          <OutdatedRevisionsToast
            title="The following workflow nodes are not using the latest prompt revision:"
            nodes={parentNodes}
            revision={data.revision}
            revisionField={(node) => promptRevisionField(node, promptId)}
            newPromptInputs={newInputs}
            removedPromptInputs={removedInputs}
            activeWorkflowId={activeWorkflowId}
            navigate={navigate}
          />,
          { autoClose: false, closeOnClick: false },
        );
      }
    } catch (e) {
      const serverFolderError = getFolderPathServerError(e);
      if (serverFolderError) {
        setFolderError(serverFolderError);
      } else if (!(e instanceof Error)) {
        toast.error('Error when updating prompt.');
      }
    }
  };

  const handleSaveAsNew = async (formData: CreatePromptRequest) => {
    setFolderError(null);
    try {
      const { id: createdPromptId, revision } = await createPromptCopy({
        ...formData,
        prompt_name:
          formData.prompt_name === prompt?.prompt_name ? `${formData.prompt_name}(copy)` : formData.prompt_name,
      });
      handleChangeRevision(revision);
      onSubmit(createdPromptId);
    } catch (e) {
      const serverFolderError = getFolderPathServerError(e);
      if (serverFolderError) {
        setFolderError(serverFolderError);
      } else if (e instanceof Error) {
        toast.error(`Error when creating prompt copy: ${e.message}`);
      } else {
        toast.error(`An unknown error occurred.`);
      }
    }
  };

  const revisionOptions = Array.from({ length: latestPrompt?.revision ?? 0 }, (_, i) => i + 1).reverse();

  const handleCopyLink = () => {
    const url = window.location.href.includes('promptId=')
      ? window.location.href
      : `${window.location.href.split('?', 1)[0]}?config=prompts&promptId=${prompt?.id}&revision=${prompt?.revision}`;

    void navigator.clipboard.writeText(url).then(() => {
      toast.success('Prompt link copied to clipboard');
    });
  };

  const handleClose = useCallback(() => {
    setSearchParams({});
    onCancel();
  }, [setSearchParams, onCancel]);

  useEffect(() => {
    if (!prompt && promptIsFetched && isRevisedPromptFetched) {
      toast.error('Prompt not found!');
      handleClose();
    }
  }, [prompt, promptIsFetched, isRevisedPromptFetched, handleClose]);

  if (promptIsLoading || isRevisedPromptLoading || areProjectsLoading) {
    return <Loader />;
  }

  const headerActions = (
    <>
      <ActionIconButton size="small" color="info" title="View usage" onClick={() => showUsageDialog(true)}>
        <FaListUl size={16} />
      </ActionIconButton>
      <ActionIconButton size="small" color="info" title="Copy link" onClick={handleCopyLink}>
        <FaLink size={16} />
      </ActionIconButton>
      <Typography component="span" variant="caption" sx={{ color: 'info.main' }}>
        revision:
      </Typography>
      <RevisionSelect
        key={selectedRevision}
        size="small"
        value={selectedRevision}
        onChange={(event) => handleChangeRevision(event.target.value as number)}
        aria-label="revision"
      >
        {revisionOptions.map((revision: number) => (
          <MenuItem key={revision} value={revision}>
            {revision}
          </MenuItem>
        ))}
      </RevisionSelect>
    </>
  );

  const sortedTests = [...(testsList ?? [])].sort((t1, t2) => (t1.llm_model_id > t2.llm_model_id ? -1 : 1));

  return (
    <>
      <TwoPaneRoot>
        <PromptFormEditor
          key={`form-${prompt?.id}-${prompt?.revision}`}
          headerActions={headerActions}
          prompt={prompt}
          projects={projects}
          requiredInputs={requiredInputs}
          additionalInputsAllowed={additionalInputsAllowed}
          templateHint={templateHint}
          isUpdatePending={isUpdatePending}
          isSaveCopyPending={isSaveCopyPending}
          latestRevisionSelected={latestRevisionSelected}
          folderError={folderError}
          onClose={handleClose}
          onSave={(formData) => void handleSavePrompt(formData)}
          onSaveAsNew={(formData) => void handleSaveAsNew(formData)}
        />

        <Pane>
          <PaneHeader>
            <PaneHeaderTitle>Prompt tests</PaneHeaderTitle>
          </PaneHeader>
          <PaneScroll>
            {sortedTests.length > 0 ? (
              <div className="flex flex-col gap-3">
                {sortedTests.map((testCase) => (
                  <PromptsTestCaseItem prompt={prompt} testCase={testCase} key={testCase.id} />
                ))}
              </div>
            ) : (
              <div className="grid h-full place-items-center px-6">
                <Typography variant="body2" color="text.secondary" className="text-center">
                  No tests yet. Add a test to run this prompt against sample inputs.
                </Typography>
              </div>
            )}
          </PaneScroll>
          <PaneFooter className="justify-end">
            <BlueButton variant="contained" startIcon={<AddIcon />} onClick={addNewTest}>
              Add New Test
            </BlueButton>
          </PaneFooter>
        </Pane>
      </TwoPaneRoot>
      <UsageDialog prompt={prompt} onClose={() => showUsageDialog(false)} />
    </>
  );
};

type PromptFormEditorProps = {
  headerActions: ReactNode;
  prompt?: Prompt;
  projects?: Project[];
  requiredInputs?: string[];
  additionalInputsAllowed?: boolean;
  templateHint?: string;
  isUpdatePending: boolean;
  isSaveCopyPending: boolean;
  latestRevisionSelected: boolean;
  folderError: string | null;
  onClose: () => void;
  onSave: (formData: CreatePromptRequest) => void;
  onSaveAsNew: (formData: CreatePromptRequest) => void;
};

const PromptFormEditor = ({
  headerActions,
  prompt,
  projects,
  requiredInputs,
  additionalInputsAllowed = true,
  templateHint,
  isUpdatePending,
  isSaveCopyPending,
  latestRevisionSelected,
  folderError,
  onSave,
  onSaveAsNew,
  onClose,
}: PromptFormEditorProps) => {
  const formRef = useRef<Form<CreatePromptRequest>>(null);
  const [formData, setFormData] = useState<CreatePromptRequest | null>(null);

  const fieldsInputs = formData?.inputs ?? prompt?.inputs ?? [];
  const canSave = formData && !isUpdatePending && !isSaveCopyPending;

  const handleOnChange = (newFormData: CreatePromptRequest) => {
    setFormData(newFormData);
  };

  const validateForm = () => {
    if (!formRef.current?.validateForm()) {
      toast.error('Form validation failed. Please check the fields.');
      return false;
    }

    if (requiredInputs?.some((input) => !formData?.inputs.includes(input))) {
      toast.error(`Prompt requires the following input fields: ${requiredInputs.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!canSave || prompt?.is_default || !latestRevisionSelected) {
      return;
    }

    if (formData && validateForm()) {
      onSave(formData);
    }
  };

  const handleSaveAsNew = () => {
    if (!canSave) {
      return;
    }

    if (formData && validateForm()) {
      onSaveAsNew(formData);
    }
  };

  return (
    <Pane divide>
      <PaneHeader>
        <PaneHeaderTitle>Edit Prompt</PaneHeaderTitle>
        {headerActions}
      </PaneHeader>
      <PaneScroll>
        <FormBlock
          formId={FORM_ID}
          initialValues={prompt}
          onSubmit={handleSave}
          onChange={handleOnChange}
          onCancel={onClose}
          schema={getFormSchema(projects, undefined, fieldsInputs, templateHint)}
          uiSchema={getUiSchema(fieldsInputs, requiredInputs, additionalInputsAllowed)}
          submitBtnTxt="save"
          cancelBtnTxt="back to list"
          cancelBtnIcon={<ChevronLeftIcon />}
          showButtons={false}
          disabled={isUpdatePending || isSaveCopyPending}
          ref={formRef}
          validator={validator}
          extraErrors={folderExtraErrors(folderError)}
        />
      </PaneScroll>
      <PaneFooter>
        <CloseTextButton className="mr-auto" onClick={onClose} disabled={isUpdatePending || isSaveCopyPending}>
          Close
        </CloseTextButton>
        <OutlinedActionButton variant="outlined" className="relative" disabled={!canSave} onClick={handleSaveAsNew}>
          {isSaveCopyPending ? <CircularProgress size={20} /> : 'Save as new'}
        </OutlinedActionButton>
        {!prompt?.is_default && latestRevisionSelected && (
          <LimeButton variant="contained" className="relative" disabled={!canSave} onClick={handleSave}>
            {isUpdatePending && <CircularProgress className="absolute left-1/2 top-1/2" size={20} />}
            Save
          </LimeButton>
        )}
      </PaneFooter>
    </Pane>
  );
};

export default PromptEdit;
